import type { AppProps } from 'next/app';
import { Assistant, Be_Vietnam_Pro, IBM_Plex_Sans } from 'next/font/google';
import { FC, useEffect, useState } from 'react';
import { Provider, connect } from 'react-redux';
import { agreeCookieNotice, setLanguage } from '../actions/baseActions';
import { useStore } from '../store';

import { useSearchParams } from 'next/navigation';
import Script from 'next/script';
import RenderGA from '../app/components/Common/RenderGA';
import Helper from '../app/modules/Helper';
import '../styles/app.scss';
import '../styles/responsive.scss';
import '../styles/variables.scss';
import '@datadirects/npm-registry/dist/index.css';

const assistant = Assistant({
	subsets: ['latin-ext'],
	display: 'swap',
	weight: ['400', '500', '600', '700', '800'],
	variable: '--font-assistant',
});

const bVietnamPro = Be_Vietnam_Pro({
	subsets: ['vietnamese'],
	display: 'swap',
	weight: ['400', '500', '600', '700', '800'],
	variable: '--font-vietnam',
});

const ibmPlexSans = IBM_Plex_Sans({
	subsets: ['latin-ext'],
	display: 'swap',
	weight: ['400', '500', '600', '700'],
	variable: '--font-ibm-plex-sans',
});

type ReduxProps = {
	currentLng?: string;
	enabledVert?: string;
	uid?: string;
	imid?: string;
	setLanguage: (lng: string) => void;
	agreeCookieNotice: () => void;
};

const App: FC<AppProps & ReduxProps> = ({
	Component,
	pageProps,
	currentLng,
	enabledVert,
	uid,
	imid,
	setLanguage,
	agreeCookieNotice,
}) => {
	const [hydrated, setHydrated] = useState(false);
	const [enableA11yScript, setEnableA11yScript] = useState(false);
	const [showCookiebotScript, setShowCookiebotScript] = useState(false);
	const [isSiteLoaded, setIsSiteLoaded] = useState(false);

	const params = useSearchParams();
	const lng = Helper.getLanguageCode(params?.get('lng') as string);

	useEffect(() => {
		if (lng && typeof document !== 'undefined') {
			document.documentElement.lang = lng;

			// Set language in Redux store
			if (currentLng !== lng) {
				setLanguage(lng);
			}

			if (Helper.isRtlLanguage(lng)) {
				document.body.classList.add('rtl');
			} else {
				document.body.classList.remove('rtl');
			}

			const langCode = Helper.getLanguageCode(lng);
			const isHebrew = langCode === 'he';
			if (isHebrew && !enableA11yScript) {
				setEnableA11yScript(true);
			}
		}
	}, [lng, currentLng, setLanguage, enableA11yScript]);


	useEffect(() => {
		let agreeCookieNoticeValue;
		try {
			agreeCookieNoticeValue = localStorage.getItem('cookieNotice');
			if (agreeCookieNoticeValue) {
				agreeCookieNotice();
			}
		} catch (error) {
			console.error('Failed to read from localStorage, fallback to default value:', error);
		}
		setHydrated(true);
	}, [agreeCookieNotice]);

	useEffect(() => {
		if (typeof window !== 'undefined') {
			setShowCookiebotScript(!window.location.pathname.includes('/passthru'));
		}

		const onLoad = () => {
			setIsSiteLoaded(true);
		};

		if (document.readyState === 'complete') {
			setIsSiteLoaded(true);
		} else {
			window.addEventListener('load', onLoad);
		}

		return () => {
			window.removeEventListener('load', onLoad);
		};
	}, []);

	if (!hydrated) {
		// Returns null on first render, so the client and server match
		return null;
	}

	return (
		<>
			<RenderGA isSiteLoaded={isSiteLoaded} enabledVert={enabledVert} uid={uid} imid={imid} />
			{/* Cookiebot loaded early (afterInteractive, not gated on full window-load)
			    so its consent update resolves before the first measurement hit (A13). */}
			{showCookiebotScript && (
				<Script
					strategy="afterInteractive"
					id="Cookiebot"
					src="https://consent.cookiebot.com/uc.js"
					data-cbid="696b4af8-081c-41e4-a5c7-ec156c9c9878"
					data-blockingmode="auto"
					type="text/javascript"
				/>
			)}
			{isSiteLoaded && (
				<>
					{enableA11yScript && (
						<Script strategy="afterInteractive" src="https://cdn.userway.org/widget.js" data-account="ckynQOSB8r" />
					)}
				</>
			)}
			<main className={assistant.variable + ' ' + bVietnamPro.variable + ' ' + ibmPlexSans.variable}>
				<Component {...pageProps} />
			</main>
		</>
	);
};

const mapStateToProps = (state: any) => ({
	currentLng: state?.base?.lng,
	enabledVert: state?.base?.enabledVert,
	uid: state?.base?.uid,
	imid: state?.base?.imid,
});

const mapDispatchToProps = {
	setLanguage,
	agreeCookieNotice,
};

const ConnectedMyApp = connect(mapStateToProps, mapDispatchToProps)(App);

function AppWithProvider({ Component, pageProps, router }: AppProps) {
	const store = useStore(pageProps.initialReduxState);
	// const { growthbook, callback: growthbookCallback } = useGrowthbook();
	// store.dispatch(setGrowthbookCallback(growthbookCallback));

	return (
		// <GrowthBookProvider growthbook={growthbook}>
		<Provider store={store}>
			<ConnectedMyApp Component={Component} pageProps={pageProps} router={router} />
		</Provider>
		// </GrowthBookProvider>
	);
}

export default AppWithProvider;
